import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Divider, Grid, Theme, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Field } from '@campfire/field';
import { Select } from '@campfire/select/lib';
import { HoverText } from '@campfire/hover-link/lib';
import { DatePickerField } from '@campfire/date-picker/lib';
import { RichEditorFormik } from '@campfire/campfire-rich-editor';
import { useFormikContext } from 'formik';
import { KeyboardArrowDown, KeyboardArrowUp, EventNote, Group } from '@material-ui/icons';
import { SwitchField } from '../dialogs/field-task-item-dialog/additional-field-type-fields/SwitchField';
import {
  CakeSelectItems,
  SaveTaskValues,
  TaskFormBuilderProgram,
  TaskFormBuilderRole,
  ExpiryRecurrence,
  TaskFormBuilderActivity,
  TaskStageSelectItems,
  TaskStage,
} from '../../admin-console-volunteer-profile-model';
import { ManualReviewDialog } from '../dialogs/ManualReviewDialog';
import { TaskProgramSelectionSection } from './TaskProgramSelectionSection';
import { TaskActivitySelectionSection } from './TaskActivitySelectionSection';
import { TaskPermissionLevelSelectionSection } from './TaskPermissionLevelSelectionSection';
import { StripeProductSelectionSection } from './StripeProductSelectionSection';
import { AlertCard } from '../../../../../../../common/cards/alert-card/AlertCard';
import { useCampfireTheme } from '../../../../../../../theme/useCampfireTheme';
import { AddTaskStageDialog } from '../dialogs/AddTaskStageDialog';

interface Props {
  cakeSelectItems: CakeSelectItems;
  taskStageSelectItems: TaskStageSelectItems;
  remainingManualTasks: number;
  isTaskZero: boolean;
  isNew: boolean;
  programOptions: Array<TaskFormBuilderProgram>;
  roleOptions: Array<TaskFormBuilderRole>;
  activityOptions: Array<TaskFormBuilderActivity>;
  permissionLevelOptions: Array<{ value: string; label: string }>;
  onTaskStageAdd: (taskStage: Omit<TaskStage, 'taskStageId'>) => Promise<string | undefined>;
  disabled?: boolean;
  taskType?: string;
}
interface FrequencyItem {
  label: string;
  value: ExpiryRecurrence;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    infoBody: {
      color: theme.alert.blue.dark,
      fontSize: '13px',
      fontWeight: 400,
    },
  })
);

export const TaskDetailsCard = ({
  cakeSelectItems,
  taskStageSelectItems,
  remainingManualTasks,
  isTaskZero,
  isNew,
  programOptions,
  activityOptions,
  roleOptions,
  permissionLevelOptions,
  onTaskStageAdd,
  disabled = false,
  taskType,
}: Props) => {
  const [showDescriptionField, setShowDescriptionField] = useState(false);
  const [showManualReviewDialog, setShowManualReviewDialog] = useState(false);
  const [isExpiryRecurrence, setIsExpiryRecurrence] = useState(false);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [showAddTaskStageDialog, setShowAddTaskStageDialog] = useState(false);
  const { theme } = useCampfireTheme();

  const { values, setFieldValue } = useFormikContext<SaveTaskValues>();

  const classes = useStyles();

  const frequencyItems: Array<FrequencyItem> = [
    {
      label: 'Monthly',
      value: 'monthly',
    },
    {
      label: 'Quarterly',
      value: 'quarterly',
    },
    {
      label: 'Half Annually',
      value: 'half_annually',
    },
    {
      label: 'Annually',
      value: 'annually',
    },
  ];

  const handleManualReviewChange = () => {
    if (!values.automateApproval && remainingManualTasks === 1) {
      setShowManualReviewDialog(true);
      return;
    }
    setFieldValue('automateApproval', !values.automateApproval);
  };

  const handleApplicantsOnlyChange = () => {
    setFieldValue('applicantsOnly', !values.applicantsOnly);
  };

  const handleIsHiddenChange = () => {
    setFieldValue('isHidden', !values.isHidden);
  };

  const handleShowExpiryRecurrence = () => {
    if (isExpiryRecurrence) {
      setFieldValue('frequency', undefined);
    } else {
      setFieldValue('frequency', 'monthly');
    }
    setIsExpiryRecurrence(!isExpiryRecurrence);
  };

  const handleAllowUpdateChange = () => {
    setFieldValue('allowUpdate', !values.allowUpdate);
  };

  const handleTaskStageAdd = async (taskStage: Omit<TaskStage, 'taskStageId'>) => {
    const newStageId = await onTaskStageAdd(taskStage);
    setShowAddTaskStageDialog(false);

    // If a new stage ID was returned, select it in the dropdown
    if (newStageId) {
      setFieldValue('taskStageId', newStageId);
    }
  };

  // Create enhanced task stage dropdown items with empty option and "Add Stage" option
  const enhancedTaskStageItems = [
    { label: 'No Task Stage selected', value: null, isEmptyOption: true },
    ...taskStageSelectItems,
    { label: '+ Add Stage', value: '__add_stage__' },
  ];

  useEffect(() => {
    if (values.description) setShowDescriptionField(true);
  }, [values.description]);

  useEffect(() => {
    if (values.frequency) setIsExpiryRecurrence(true);
  }, [values.frequency]);

  useEffect(() => {
    // Handle "Add Stage" selection
    if (values.taskStageId === '__add_stage__') {
      setShowAddTaskStageDialog(true);
      setFieldValue('taskStageId', null); // Reset to null
    }
  }, [values.taskStageId, setFieldValue]);

  return (
    <Card>
      <CardContent style={{ padding: 48, paddingTop: 32, paddingBottom: 15 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display='flex' alignItems='center'>
              {taskType === 'event' && <EventNote style={{ marginRight: 8, color: '#666666', fontSize: '20px' }} />}
              {taskType === 'referee_check' && <Group style={{ marginRight: 8, color: '#666666', fontSize: '20px' }} />}
              <Typography style={{ fontSize: '16px', fontWeight: 600, color: '#666666' }}>
                {taskType === 'event'
                  ? 'Scheduled Event Details'
                  : taskType === 'referee_check'
                  ? 'Referee Check Details'
                  : values.managerCompleted
                  ? 'Manager Task Details'
                  : 'Task Details'}
              </Typography>
            </Box>
          </Grid>
          {taskType === 'event' && (
            <Grid item xs={12} style={{ marginBottom: '12px' }}>
              <AlertCard variant='info' title='Scheduled Event Tasks'>
                <Typography className={classes.infoBody}>
                  Scheduled event tasks allow you to organise and track meetings, interviews, training sessions, and
                  other scheduled activities with volunteers. Managers can schedule events, assign coordinators, and
                  record outcomes.
                  <br />
                  <br />
                  Events can be conducted in-person at a physical location or remotely via video call. Times and
                  locations are organised in the Incoming Vols tab.
                </Typography>
              </AlertCard>
            </Grid>
          )}
          {taskType === 'referee_check' && (
            <Grid item xs={12} style={{ marginBottom: '12px' }}>
              <AlertCard variant='info' title='Referee Check Tasks'>
                <Typography className={classes.infoBody}>
                  Referee check tasks enable you to collect references from external contacts about volunteers. You can
                  specify the number of referees required and create custom fields for the information you need.
                  <br />
                  <br />
                  Volunteers provide referee contact details, and referees receive a secure link to submit their
                  references online.
                </Typography>
              </AlertCard>
            </Grid>
          )}
          {taskType === 'stripe_product' && (
            <Grid item xs={12} style={{ marginBottom: '12px' }}>
              <AlertCard variant='info' title='Payment Tasks'>
                <Typography className={classes.infoBody}>
                  Payment tasks allow you to collect payments from volunteers using Stripe. Volunteers will be
                  redirected to secure Stripe Checkout to complete their payment.
                  <br />
                  <br />
                  You can configure one-time payments or recurring subscriptions, and tasks can be automatically
                  completed when payment is successful.
                </Typography>
              </AlertCard>
            </Grid>
          )}
          <Grid item xs={12}>
            <Field
              autoFocus
              name='title'
              label={isNew ? 'This task will be named' : 'Task name'}
              placeholder={
                taskType === 'event'
                  ? 'e.g., "Volunteer Interview", "Training Session", "Orientation Meeting"'
                  : taskType === 'referee_check'
                  ? 'e.g., "Reference Check", "Character References"'
                  : undefined
              }
              required
              fullWidth
              variant='outlined'
              disabled={isTaskZero}
            />
          </Grid>
          {taskType === 'referee_check' && (
            <>
              <Grid item xs={12}>
                <Field
                  name='refereeChecksRequired'
                  label='Number of referee checks required'
                  type='number'
                  inputProps={{ min: 1, max: 10 }}
                  placeholder='e.g., 2'
                  helperText='How many referees should each volunteer provide?'
                  required
                  fullWidth
                  variant='outlined'
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' style={{ marginBottom: '8px', fontWeight: 500 }}>
                  Instructions for Referees (Optional)
                </Typography>
                <Typography variant='caption' color='textSecondary' style={{ marginBottom: '12px', display: 'block' }}>
                  These instructions will be shown to referees when they fill out the form
                </Typography>
                <RichEditorFormik
                  valueName='refereeInstructions'
                  placeholder='Provide any special instructions for referees...'
                />
              </Grid>
            </>
          )}
          {taskType === 'stripe_product' && <StripeProductSelectionSection />}
          {taskType !== 'interview' && taskType !== 'referee_check' && taskType !== 'stripe_product' && (
            <Grid item xs={12}>
              <Select
                fullWidth
                slow
                label={isNew ? 'Add to' : 'This task is'}
                name='cakeId'
                variant='outlined'
                disabled={disabled || isTaskZero}
                items={cakeSelectItems}
                renderFunction={(item) => item.label}
                valueFunction={(item) => item.value}
              />
            </Grid>
          )}
          {!isTaskZero && (
            <Grid item xs={12}>
              <Select
                fullWidth
                slow
                label='Task Stage (Optional)'
                name='taskStageId'
                variant='outlined'
                placeholder='No stage assigned'
                disabled={disabled}
                items={enhancedTaskStageItems}
                renderFunction={(item) => (
                  <span style={{ fontStyle: item.isEmptyOption ? 'italic' : 'normal' }}>{item.label}</span>
                )}
                valueFunction={(item) => item.value}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value: any) => {
                    if (value === null || value === undefined) {
                      return <span style={{ color: '#9e9e9e' }}>No Task Stage selected</span>;
                    }
                    const selectedItem = enhancedTaskStageItems.find((item) => item.value === value);
                    return selectedItem ? selectedItem.label : value;
                  },
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            {!showDescriptionField ? (
              <HoverText onClick={() => setShowDescriptionField(true)} color='primary'>
                {'Add description'}
              </HoverText>
            ) : null}
          </Grid>
          {showDescriptionField ? (
            <Grid item xs={12}>
              <Field fullWidth label='Description' name='description' variant='outlined' />
            </Grid>
          ) : null}

          {isTaskZero && !values.managerCompleted && (
            <Grid item xs={12} style={{ paddingTop: 0, marginTop: '6px' }}>
              <SwitchField
                title='Manual Review'
                description={
                  !values.automateApproval
                    ? 'Task submissions will require manual approval'
                    : 'No manual review is required for this task'
                }
                checked={!values.automateApproval}
                onChange={handleManualReviewChange}
                disabled={disabled}
              />
            </Grid>
          )}

          {!isTaskZero ? (
            <>
              <Grid item xs={12} style={{ padding: '25px 0' }}>
                <Divider />
              </Grid>
              <Grid item container xs={12}>
                <Typography style={{ fontSize: '15px', fontWeight: 600, color: '#666666', paddingBottom: '10px' }}>
                  Options
                </Typography>
                {!values.managerCompleted &&
                  (taskType === 'referee_check' ||
                    !taskType ||
                    (taskType !== 'event' && taskType !== 'referee_check' && taskType !== 'stripe_product')) && (
                    <Grid item xs={12}>
                      <SwitchField
                        title='Manual Review'
                        description={
                          !values.automateApproval
                            ? 'Task submissions will require manual approval'
                            : 'No manual review is required for this task'
                        }
                        checked={!values.automateApproval}
                        onChange={handleManualReviewChange}
                        disabled={disabled}
                      />
                    </Grid>
                  )}
                {taskType !== 'interview' &&
                  taskType !== 'referee_check' &&
                  taskType !== 'event' &&
                  taskType !== 'stripe_product' && (
                    <Grid item xs={12} style={{ marginTop: '12px' }}>
                      <SwitchField
                        title='Task expiry'
                        description={'Set this task to expire on a selected schedule'}
                        checked={isExpiryRecurrence}
                        onChange={handleShowExpiryRecurrence}
                        disabled={disabled}
                      />
                    </Grid>
                  )}
                {isExpiryRecurrence && (
                  <Box paddingTop={'15px'} paddingBottom={'15px'}>
                    <AlertCard variant='info' title='Task Expiry'>
                      <Typography className={classes.infoBody}>
                        Setting a task expiration means that volunteers are asked to recomplete a task each time the
                        start date and frequency conditions are met.
                        <br />
                        <br />
                        <i>
                          <strong>eg. </strong>A &apos;Training Task&apos; having an annual frequency, with the start
                          date of January 1st, will make the volunteer recomplete the task at the beginning of every
                          year.
                        </i>
                        <br />
                        <br />
                        Date fields within tasks can also act as a task expiry (
                        <i>
                          <b>eg.</b> Drivers Licences
                        </i>
                        ), which can be configured via the field settings.
                      </Typography>
                    </AlertCard>
                    <Grid item xs={12} style={{ marginTop: '24px' }}>
                      <DatePickerField
                        name='startDate'
                        label='Start Date'
                        autoOk
                        fullWidth
                        inputVariant='outlined'
                        required
                        disabled={disabled}
                      />
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: '24px' }}>
                      <Select
                        fullWidth
                        slow
                        label={'Frequency'}
                        name='frequency'
                        variant='outlined'
                        disabled={disabled}
                        items={frequencyItems}
                        renderFunction={(item) => item.label}
                        valueFunction={(item) => item.value}
                      />
                    </Grid>
                  </Box>
                )}
                {taskType !== 'interview' && (
                  <Grid item xs={12} style={{ marginTop: '12px' }}>
                    <SwitchField
                      title='Admin Only Visibility'
                      description={`Submissions will be visible only to administrator users`}
                      checked={values.adminOnly}
                      onChange={() => {
                        setFieldValue('adminOnly', !values.adminOnly);
                      }}
                      disabled={disabled}
                    />
                  </Grid>
                )}
              </Grid>
            </>
          ) : null}
          {!values.managerCompleted &&
            taskType !== 'interview' &&
            taskType !== 'event' &&
            taskType !== 'referee_check' &&
            taskType !== 'stripe_product' && (
              <Grid item xs={12} style={{ paddingTop: 0, marginTop: '6px' }}>
                <SwitchField
                  title='Volunteers can Update'
                  description='Volunteers can edit this task at any time after submission. You will be alerted that volunteers have updated the task if manual approval is turned on, and have the option to revert to a previous state if necessary.'
                  checked={values.allowUpdate}
                  onChange={handleAllowUpdateChange}
                  disabled={disabled}
                />
              </Grid>
            )}
          {!isTaskZero ? (
            <>
              <Grid item xs={12} style={{ padding: '25px 0' }}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography style={{ fontSize: '15px', fontWeight: 700, color: '#666666' }}>Visibility</Typography>
                <Typography style={{ fontSize: '13px', fontWeight: 400, color: '#9E9E9E' }}>
                  Select programs and roles below to display the task to certain volunteers only.
                  <br />
                  Default settings will display the task to all volunteers.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TaskProgramSelectionSection programOptions={programOptions} />
              </Grid>
              <Grid item xs={12}>
                <TaskActivitySelectionSection activityOptions={activityOptions} />
              </Grid>
              <Grid item xs={12}>
                <TaskPermissionLevelSelectionSection roleOptions={roleOptions} />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  paddingTop: '0 !important',
                  paddingBottom: '0 !important',
                }}
              >
                <Button
                  variant='text'
                  endIcon={showAdvanced ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  style={{
                    fontSize: 10,
                    textDecoration: 'none',
                    color: theme.color.grey.neutral300,
                    textTransform: 'none',
                    padding: 0,
                  }}
                >
                  Advanced
                </Button>
              </Grid>
              {showAdvanced ? (
                <>
                  <Grid item xs={12}>
                    <Box mt={3}>
                      <Typography
                        style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '10px' }}
                      >
                        PERMISSION LEVEL
                      </Typography>
                      <Select
                        fullWidth
                        variant='outlined'
                        name='permissionLevel'
                        placeholder='No permission level (Visible to all)'
                        label='Select permission level'
                        items={permissionLevelOptions}
                        renderFunction={(item) => item.label}
                        valueFunction={(item) => item.value}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} style={{ marginTop: '10px' }}>
                    <SwitchField
                      title='Applicants Only'
                      description={
                        'This task will display only for new volunteers signing up to Volaby. Existing volunteers, who have been approved, will not be able to complete this task.'
                      }
                      checked={values.applicantsOnly}
                      onChange={handleApplicantsOnlyChange}
                      disabled={disabled}
                    />
                  </Grid>
                  <Grid item xs={6} style={{ marginTop: '10px' }}>
                    <SwitchField
                      title='Hidden Task'
                      description={'This task will not display for volunteers who have not yet completed it.'}
                      checked={values.isHidden}
                      onChange={handleIsHiddenChange}
                      disabled={disabled}
                    />
                  </Grid>
                </>
              ) : null}
              <Grid item style={{ paddingBottom: '25px' }}></Grid>
            </>
          ) : null}
          <ManualReviewDialog open={showManualReviewDialog} close={() => setShowManualReviewDialog(false)} />
          <AddTaskStageDialog
            open={showAddTaskStageDialog}
            onClose={() => setShowAddTaskStageDialog(false)}
            onSubmit={handleTaskStageAdd}
          />
        </Grid>
      </CardContent>
    </Card>
  );
};
